/**
 * B31h (TRA-80) — the party composer, pure. The domain already holds
 * multi-line multi-slot atomic carts (B4/B11); this module is the UI-side
 * vocabulary that finally exposes it: bounded quantity steppers, §4.2
 * constraint HINTS at composition time (buyer language, pairing with
 * B31g's translation stance — wall vocabulary never renders here), live
 * line prices from the card's channel display prices (honest absence
 * preserved), and the add plan — one cart.add per composed variant, all
 * riding ONE slot so checkout's atomic hold covers the whole party.
 *
 * Flow order is CONFIGURATION (presentation.flowOrder on the card;
 * absent = SLOT_FIRST): ferries/events pick the departure then compose,
 * attractions/tours compose then pick a departure. Availability stays
 * honest: the card never carries the §2.4 consumption matrix, so the
 * composer never fabricates a fit — cart.add remains the wall (Inv 2/3).
 */
import type { Money } from "./money.js";
import type { ProductSummary, VariantCard } from "../ui/screens.js";

export type FlowOrder = "SLOT_FIRST" | "TICKETS_FIRST";

/** variantId → quantity ≥ 0. Absent = 0. */
export type Composition = Readonly<Record<string, number>>;

/** Storefront cap when the product sets no maxPerOrder — a bounded stepper, not a wall pretending to be capacity. */
export const MAX_PER_LINE = 20;

export function flowOrderOf(card: Pick<ProductSummary, "presentation">): FlowOrder {
  return card.presentation?.flowOrder ?? "SLOT_FIRST";
}

export function emptyComposition(): Composition {
  return {};
}

export function quantityOf(comp: Composition, variantId: string): number {
  return comp[variantId] ?? 0;
}

function variantOf(card: ProductSummary, variantId: string): VariantCard {
  const v = card.variants.find((x) => x.variantId === variantId);
  if (v === undefined) throw new Error(`unknown variant: ${variantId} on ${card.productId}`);
  return v;
}

export function maxQuantity(card: ProductSummary, variantId: string): number {
  return variantOf(card, variantId).constraints?.maxPerOrder ?? MAX_PER_LINE;
}

/**
 * B580 — the most a stepper may hold for one type. A type's own "Most per
 * order" under the channel's group threshold is where a party BECOMES a
 * group, so while groups are on (threshold > 0) one past that cap is
 * reachable: it is the press that opens the groups desk (B549's panel),
 * never a line the cart will take. Groups off — or a cap at or above the
 * threshold, which the threshold already governs — keeps the plain cap.
 */
export function orderCap(card: ProductSummary, variantId: string, groupThreshold?: number): number {
  const cap = variantOf(card, variantId).constraints?.maxPerOrder;
  if (cap !== undefined && groupThreshold !== undefined && groupThreshold > 0 && cap < groupThreshold) return cap + 1;
  return maxQuantity(card, variantId);
}

/**
 * Set a variant's quantity. Unknown variants and illegal numbers are walls;
 * the max-per-order bound clamps — that IS the + button's inline wall.
 * B580: given the channel's group threshold, the bound is `orderCap`'s.
 */
export function setQuantity(card: ProductSummary, comp: Composition, variantId: string, qty: number, groupThreshold?: number): Composition {
  if (!Number.isSafeInteger(qty) || qty < 0) throw new Error(`illegal quantity: ${qty}`);
  const bounded = Math.min(qty, orderCap(card, variantId, groupThreshold));
  return { ...comp, [variantId]: bounded };
}

export function totalQuantity(comp: Composition): number {
  return Object.values(comp).reduce((n, q) => n + q, 0);
}

export interface CompositionLine {
  readonly variantId: string;
  readonly name: string;
  readonly quantity: number;
  /** quantity × displayPrice — absent when the channel resolves no price (honest absence). */
  readonly linePrice?: Money;
}

/** Nonzero lines in card order — what the composer and the cart preview render. */
export function compositionLines(card: ProductSummary, comp: Composition): readonly CompositionLine[] {
  return card.variants
    .filter((v) => quantityOf(comp, v.variantId) > 0)
    .map((v) => {
      const quantity = quantityOf(comp, v.variantId);
      const price = v.displayPrice;
      return {
        variantId: v.variantId,
        name: v.name,
        quantity,
        ...(price !== undefined
          ? { linePrice: { amount: price.amount * quantity, currency: price.currency } }
          : {}),
      };
    });
}

export type CompositionIssue =
  | { readonly kind: "REQUIRES"; readonly variantId: string; readonly requiresVariantId: string }
  | { readonly kind: "MIN_PER_ORDER"; readonly variantId: string; readonly min: number };

/**
 * §4.2 hints AT COMPOSITION TIME: requires-variant unmet and below-minimum
 * quantities surface while the party is being set, so the buyer never
 * meets these as checkout refusals.
 */
export function compositionIssues(card: ProductSummary, comp: Composition): readonly CompositionIssue[] {
  const issues: CompositionIssue[] = [];
  for (const v of card.variants) {
    const qty = quantityOf(comp, v.variantId);
    if (qty === 0) continue;
    const requires = v.constraints?.requiresVariantId;
    if (requires !== undefined && quantityOf(comp, requires) === 0) {
      issues.push({ kind: "REQUIRES", variantId: v.variantId, requiresVariantId: requires });
    }
    const min = v.constraints?.minPerOrder;
    if (min !== undefined && qty < min) {
      issues.push({ kind: "MIN_PER_ORDER", variantId: v.variantId, min });
    }
  }
  return issues;
}

/** Buyer-language hint copy — wall vocabulary never renders in the buyer UI. */
export function issueHint(issue: CompositionIssue, card: ProductSummary): string {
  const name = (variantId: string): string =>
    card.variants.find((v) => v.variantId === variantId)?.name ?? variantId;
  switch (issue.kind) {
    case "REQUIRES":
      return `A ${name(issue.variantId)} ticket needs ${aOrAn(name(issue.requiresVariantId))} in the same order.`;
    case "MIN_PER_ORDER":
      return `${name(issue.variantId)} needs at least ${issue.min} per order.`;
  }
}

function aOrAn(noun: string): string {
  return /^[aeiou]/i.test(noun) ? `an ${noun}` : `a ${noun}`;
}

export function canAdd(card: ProductSummary, comp: Composition): boolean {
  return totalQuantity(comp) > 0 && compositionIssues(card, comp).length === 0;
}

/* ------------------------------------------------------------------ B31g */

export interface CartHintLine {
  readonly lineId: string;
  readonly productId: string;
  readonly variantId?: string;
  readonly quantity: number;
}

export interface CartIssueHint {
  /** Every cart line the hint anchors to (the offending lines). */
  readonly lineIds: readonly string[];
  /** Buyer copy — wall vocabulary never renders (B31g, the B32b rule for buyers). */
  readonly message: string;
}

/**
 * B31g — the §4.2 walls the KERNEL enforces at checkout ("in the same
 * order": requires/min/max count ORDER totals across lines), spoken at the
 * offending cart lines before the buyer ever meets "Cart cannot be sold as
 * constituted". Advisory by design: the kernel stays the wall; a product
 * missing from the cards yields no hint rather than a guess.
 */
export function cartIssueHints(
  lines: readonly CartHintLine[],
  cards: readonly ProductSummary[],
): readonly CartIssueHint[] {
  const cardOf = new Map(cards.map((c) => [c.productId, c]));
  const totals = new Map<string, number>();
  const lineIdsOf = new Map<string, string[]>();
  const key = (productId: string, variantId: string): string => `${productId}\u0000${variantId}`;
  for (const line of lines) {
    if (line.variantId === undefined) continue;
    const k = key(line.productId, line.variantId);
    totals.set(k, (totals.get(k) ?? 0) + line.quantity);
    lineIdsOf.set(k, [...(lineIdsOf.get(k) ?? []), line.lineId]);
  }
  const hints: CartIssueHint[] = [];
  const seen = new Set<string>();
  for (const line of lines) {
    if (line.variantId === undefined) continue;
    const k = key(line.productId, line.variantId);
    if (seen.has(k)) continue;
    seen.add(k);
    const card = cardOf.get(line.productId);
    const variant = card?.variants.find((v) => v.variantId === line.variantId);
    if (card === undefined || variant === undefined) continue;
    const name = (variantId: string): string => card.variants.find((v) => v.variantId === variantId)?.name ?? variantId;
    const total = totals.get(k) ?? 0;
    const requires = variant.constraints?.requiresVariantId;
    if (requires !== undefined && total > 0 && (totals.get(key(line.productId, requires)) ?? 0) < 1) {
      hints.push({
        lineIds: lineIdsOf.get(k) ?? [],
        message: `A ${name(line.variantId)} ticket needs ${aOrAn(name(requires))} in the same order.`,
      });
    }
    const min = variant.constraints?.minPerOrder;
    if (min !== undefined && total > 0 && total < min) {
      hints.push({ lineIds: lineIdsOf.get(k) ?? [], message: `${name(line.variantId)} needs at least ${min} per order.` });
    }
    const max = variant.constraints?.maxPerOrder;
    if (max !== undefined && total > max) {
      hints.push({
        lineIds: lineIdsOf.get(k) ?? [],
        message: `You can order at most ${max} ${name(line.variantId)} tickets — please reduce the quantity.`,
      });
    }
  }
  return hints;
}

export interface AddPlanLine {
  readonly productId: string;
  readonly variantId: string;
  readonly quantity: number;
  readonly slotId?: string;
}

/** One cart.add per composed variant; every line rides the SAME slot (atomic party hold at checkout). */
export function addPlan(card: ProductSummary, comp: Composition, slotId?: string): readonly AddPlanLine[] {
  return compositionLines(card, comp).map((l) => ({
    productId: card.productId,
    variantId: l.variantId,
    quantity: l.quantity,
    ...(slotId !== undefined ? { slotId } : {}),
  }));
}
